import { type CallWindow, type Event } from '@kalos/kalos-rpc';
import {
  Button,
  Calendar,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  LoadingIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TimePicker,
} from '@kalos/ui';
import { addDays, differenceInMinutes, format, getDay, parseISO } from 'date-fns';
import { ChevronDown, Lock } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MAINTENANCE_JOB_TYPE_ID } from '../../../../constants';
import { useDispatchableTechsQuery } from '../../../../hooks/react-query/useDispatchQuery';
import { useJobTypesQuery } from '../../../../hooks/react-query/useJobTypesQuery';
import {
  getBlockedJobTypesForWindow,
  getBlocksForDate,
} from '../../../../hooks/react-query/useWindowBlockQuery';
import { resolveWindowLimits, type WindowLimitOverride } from '../../../../tools/callWindowLimits';
import { formatTimeCondensed, formatTimeRangeCondensed } from '../../../../tools/formatTimeUtils';
import {
  areAllWindowsPast,
  dateToTime,
  findFirstAvailableWindow,
  isEventInWindow,
  isOvernightRange,
  isTimeRangePast,
  isValidTimeRange,
  isWindowPast,
  normalizeTime,
  resolveEventWindow,
  snapToStep,
  TIME_STEP_MINUTES,
  timeToDate,
} from '../../../../tools/timeUtils';
import { CallWindowBreakout } from '../../CallWindow/CallWindowBreakout';
import { type DateStatusBreakout } from '../../CallWindow/utils';
import { JobWindowCard } from './JobWindowCard';

export interface CallWindowAvailabilityProps {
  /** Selected date in ISO format (yyyy-MM-dd) */
  selectedDate: string;
  /** All available call windows */
  callWindows: CallWindow[];
  /** Currently selected time window */
  selectedWindow: CallWindow | null;
  /** Department ID for filtering technicians */
  departmentId?: number;
  /** Callback when a window is selected */
  onSelectWindow: (window: CallWindow) => void;
  /** Callback for date navigation */
  onChangeDate: (date: string) => void;
  /** Existing event being edited (to exclude from counts) */
  existingEvent?: Event;
  /** Pre-calculated window event counts from parent */
  windowEventCounts?: Record<number, Record<number, number>>;
  /** Pre-calculated window tech counts from parent */
  windowTechCounts?: Record<number, Record<number, number>>;
  /** Pre-calculated per-job-type counts from parent: window -> dept -> jobType -> count */
  windowJobTypeBreakdown?: Record<number, Record<number, Record<string, number>>>;
  /** Pre-calculated confirmed-maintenance counts from parent: window -> dept -> count */
  windowMaintConfirmedCounts?: Record<number, Record<number, number>>;
  /** Per-department limit overrides for the booking dept: call_window_id -> effective limits */
  windowLimitOverrides?: Record<number, WindowLimitOverride>;
  /**
   * Date overrides keyed `${callWindowId}:${yyyy-MM-dd}`, covering at least selectedDate.
   * Outrank the weekly overrides above for the dates they cover.
   */
  windowLimitDateOverrides?: Record<string, WindowLimitOverride>;
  /**
   * The daily job-type cap in play for this booking, or null when nothing constrains it.
   * Day-level rather than per-window: once `reached`, no window on this day can take the job.
   */
  dailyCapState?: { max: number; used: number; jobTypeName: string; reached: boolean } | null;
  /** Job type being booked (jobTypeId); 3 = Maintenance. Drives the maintenance hard block. */
  bookingJobTypeId?: number;
  /** Callback when custom time is set */
  onCustomTime?: (startTime: string, endTime: string) => void;
  /** Current start time from form */
  currentStartTime?: string;
  /** Current end time from form */
  currentEndTime?: string;
  /** Whether capacity data is still loading (prevents premature recommendations) */
  isCapacityLoading?: boolean;
  isCustomTime?: boolean;
  /** Window block events for the current date range */
  windowBlocks?: Event[];
  /** Daily category pill breakout for the selected date (matches the week-view header). */
  dateBreakout?: DateStatusBreakout;
}

export const CallWindowAvailability: FC<CallWindowAvailabilityProps> = ({
  selectedDate,
  callWindows,
  selectedWindow,
  departmentId,
  onSelectWindow,
  onChangeDate,
  existingEvent,
  windowEventCounts = {},
  windowTechCounts = {},
  windowJobTypeBreakdown = {},
  windowMaintConfirmedCounts = {},
  windowLimitOverrides = {},
  windowLimitDateOverrides = {},
  dailyCapState = null,
  bookingJobTypeId,
  onCustomTime,
  currentStartTime,
  currentEndTime,
  isCapacityLoading = false,
  isCustomTime = false,
  windowBlocks = [],
  dateBreakout,
}) => {
  const { data: jobTypesData } = useJobTypesQuery();
  const totalJobTypeCount = jobTypesData?.length ?? 0;

  // Tab state for Business/After Hours — always default to Business
  const [activeTab, setActiveTab] = useState<'business' | 'after'>('business');

  // Custom time dialog state
  const [showCustomTimeDialog, setShowCustomTimeDialog] = useState(false);
  const [customStartTime, setCustomStartTime] = useState('08:00');
  const [customEndTime, setCustomEndTime] = useState('09:00');
  const [customTimeError, setCustomTimeError] = useState<string | null>(null);

  // Fetch dispatchable techs filtered by department
  const { data: techsData, isLoading: isLoadingTechs } = useDispatchableTechsQuery({
    filter: departmentId
      ? {
          departmentList: departmentId.toString(),
        }
      : {},
    enabled: !!departmentId,
  });

  const techs = useMemo(() => techsData?.results ?? [], [techsData?.results]);
  const loading = isLoadingTechs;

  // Get current time to determine past windows
  const now = useMemo(() => new Date(), []);
  const currentDate = format(now, 'yyyy-MM-dd');
  const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

  // Check if current custom time range is past (for warning display)
  // Uses isTimeRangePast to handle overnight bookings (e.g., 6pm-3am)
  const isCurrentCustomTimePast =
    isCustomTime &&
    currentStartTime &&
    currentEndTime &&
    isTimeRangePast(
      currentStartTime,
      currentEndTime,
      selectedDate,
      currentDate,
      currentTimeInMinutes,
    );

  // Check if the current custom time is an overnight booking
  const isCustomTimeOvernight =
    currentStartTime && currentEndTime && isOvernightRange(currentStartTime, currentEndTime);

  // Calculate tech availability for each window
  const calculateTechAvailability = useCallback(
    (window: CallWindow) => {
      let onStandby = 0;
      let finishingSoon = 0;

      techs.forEach((tech) => {
        // Check if tech is available (Standby or unassigned)
        if (tech.activity === 'Standby' || !tech.activity) {
          onStandby++;
        }
        // Check if tech is finishing soon (On Call for 60+ minutes)
        else if (tech.activity === 'On Call' && tech.activityDate) {
          const activityStartTime = parseISO(tech.activityDate);
          const minutesOnCall = differenceInMinutes(now, activityStartTime);
          if (minutesOnCall >= 60) {
            finishingSoon++;
          }
        }
      });

      return { onStandby, finishingSoon };
    },
    [techs, now],
  );

  // Get windows for the selected date
  const dayOfWeek = getDay(parseISO(selectedDate));
  const dayWindows = useMemo(
    () =>
      callWindows
        .filter((w) => Number(w.dayOfWeek) === dayOfWeek)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [callWindows, dayOfWeek],
  );
  const dayWindowsDescending = useMemo(
    () => [...dayWindows].sort((a, b) => b.startTime.localeCompare(a.startTime)),
    [dayWindows],
  );

  // The window the event under edit currently sits in. Independent of which card is being
  // rendered, so it is resolved once here rather than per card.
  const existingEventWindow = useMemo(
    () => resolveEventWindow(existingEvent, dayWindows),
    [existingEvent, dayWindows],
  );

  // Check if all preset windows are past for the selected date
  const allWindowsPast = useMemo(() => {
    return areAllWindowsPast(dayWindows, selectedDate, currentDate, currentTimeInMinutes);
  }, [dayWindows, selectedDate, currentDate, currentTimeInMinutes]);

  // Find first available window (non-past) - fallback when no tech availability
  const firstAvailableWindowMemo = useMemo(() => {
    return findFirstAvailableWindow(dayWindows, selectedDate, currentDate, currentTimeInMinutes);
  }, [dayWindows, selectedDate, currentDate, currentTimeInMinutes]);

  // Split windows into business (before 5pm) and after hours (5pm and after)
  const businessWindows = useMemo(
    () => dayWindows.filter((w) => Number(w.startTime.split(':')[0]) < 17),
    [dayWindows],
  );

  const afterHoursWindows = useMemo(
    () => dayWindows.filter((w) => Number(w.startTime.split(':')[0]) >= 17),
    [dayWindows],
  );

  // Get filtered windows based on active tab
  const filteredWindows = activeTab === 'business' ? businessWindows : afterHoursWindows;

  // Pre-compute blocks for the selected date, filtered by the current department
  // so blocks on other departments don't mark windows as blocked in this dialog.
  const blocksForDate = useMemo(
    () =>
      getBlocksForDate(windowBlocks, selectedDate).filter(
        (b) => !departmentId || b.departmentId === departmentId,
      ),
    [windowBlocks, selectedDate, departmentId],
  );

  // Returns true only if the window is FULLY blocked (all job types or all-type block).
  // Partially blocked windows (specific job types only) are still schedulable.
  const isFullyBlockedWindow = useCallback(
    (startTime: string, endTime: string) => {
      const ids = getBlockedJobTypesForWindow(blocksForDate, startTime, endTime);
      if (ids.length === 0) return false;
      if (ids.some((id) => id === 0)) return true;
      return totalJobTypeCount > 0 && ids.length >= totalJobTypeCount;
    },
    [blocksForDate, totalJobTypeCount],
  );

  // Determine recommended window
  // Don't recommend until capacity data is loaded to avoid race conditions
  const recommendedWindow = useMemo(() => {
    // Wait for capacity data to be loaded before making recommendations
    if (isCapacityLoading) {
      return null;
    }
    if (!departmentId || dayWindows.length === 0) {
      return null;
    }

    // Find first non-past, non-blocked window with available techs
    for (const window of dayWindows) {
      const isPast = isWindowPast(window, selectedDate, currentDate, currentTimeInMinutes);
      const blocked = isFullyBlockedWindow(window.startTime, window.endTime);

      if (!isPast && !blocked) {
        const { onStandby, finishingSoon } = calculateTechAvailability(window);
        if (onStandby > 0 || finishingSoon > 0) {
          return window.id;
        }
      }
    }

    // Fallback: return first available window even without tech availability
    if (firstAvailableWindowMemo) {
      const blocked = isFullyBlockedWindow(
        firstAvailableWindowMemo.startTime,
        firstAvailableWindowMemo.endTime,
      );
      if (!blocked) {
        return firstAvailableWindowMemo.id;
      }
    }

    return null;
  }, [
    isCapacityLoading,
    dayWindows,
    departmentId,
    selectedDate,
    currentDate,
    currentTimeInMinutes,
    calculateTechAvailability,
    firstAvailableWindowMemo,
    isFullyBlockedWindow,
  ]);

  // Track previous date to detect date changes
  const previousDateRef = useRef(selectedDate);

  // Auto-select the recommended window when:
  // 1. Current selection is null or full
  // 2. Date has changed (always switch to recommended on date change)
  useEffect(() => {
    if (isCustomTime) {
      return;
    }

    if (!recommendedWindow) {
      return;
    }

    const dateChanged = previousDateRef.current !== selectedDate;
    previousDateRef.current = selectedDate;

    const isCurrentSelectionInvalid = !selectedWindow;

    // Auto-select recommended window if:
    // - Current selection is invalid (null), OR
    // - Date has changed (always select best available on new date)
    if (isCurrentSelectionInvalid || dateChanged) {
      const recWindow = dayWindows.find((w) => w.id === recommendedWindow);
      if (recWindow && recWindow.id !== selectedWindow?.id) {
        onSelectWindow(recWindow);
      }
    }
  }, [
    recommendedWindow,
    selectedWindow,
    dayWindows,
    selectedDate,
    currentDate,
    currentTimeInMinutes,
    onSelectWindow,
    departmentId,
    isCustomTime,
  ]);

  // Date navigation handlers
  const handlePreviousDay = () => {
    const prevDate = format(addDays(parseISO(selectedDate), -1), 'yyyy-MM-dd');
    onChangeDate(prevDate);
  };

  const handleNextDay = () => {
    const nextDate = format(addDays(parseISO(selectedDate), 1), 'yyyy-MM-dd');
    onChangeDate(nextDate);
  };

  // Custom time handlers
  const handleCustomTimeOpen = useCallback(() => {
    // Use current form times if available, otherwise use selected window times or defaults
    const defaultStart =
      currentStartTime ||
      (selectedWindow?.startTime ? normalizeTime(selectedWindow.startTime) : undefined) ||
      '08:00';
    const defaultEnd =
      currentEndTime ||
      (selectedWindow?.endTime ? normalizeTime(selectedWindow.endTime) : undefined) ||
      '09:00';
    setCustomStartTime(snapToStep(defaultStart));
    setCustomEndTime(snapToStep(defaultEnd));
    setCustomTimeError(null); // Clear previous errors
    setShowCustomTimeDialog(true);
  }, [currentStartTime, currentEndTime, selectedWindow]);

  // Clear error when times change
  const handleStartTimeChange = useCallback((value: string) => {
    setCustomStartTime(value);
    setCustomTimeError(null);
  }, []);

  const handleEndTimeChange = useCallback((value: string) => {
    setCustomEndTime(value);
    setCustomTimeError(null);
  }, []);

  const handleCustomTimeConfirm = useCallback(() => {
    // Validate that start and end times are different (non-zero duration)
    if (!isValidTimeRange(customStartTime, customEndTime)) {
      setCustomTimeError('Start and end times cannot be the same.');
      return;
    }

    // Validate the time range is not in the past FIRST
    // Uses isTimeRangePast which handles overnight bookings correctly
    // For overnight range (e.g., 6pm-3am), it checks if today is past the end time on the NEXT day
    if (
      isTimeRangePast(
        customStartTime,
        customEndTime,
        selectedDate,
        currentDate,
        currentTimeInMinutes,
      )
    ) {
      setCustomTimeError(
        selectedDate < currentDate
          ? 'Cannot schedule for a past date. Please select a future date.'
          : 'The selected time range has already passed. Please choose a later time.',
      );
      return;
    }

    // If custom time matches a preset window, block if that window is full or blocked
    const matchingWindow = dayWindows.find(
      (w) =>
        normalizeTime(w.startTime) === normalizeTime(customStartTime) &&
        normalizeTime(w.endTime) === normalizeTime(customEndTime),
    );
    if (matchingWindow) {
      if (isFullyBlockedWindow(matchingWindow.startTime, matchingWindow.endTime)) {
        setCustomTimeError(
          `That time window (${formatTimeRangeCondensed(matchingWindow.startTime, matchingWindow.endTime)}) is currently blocked. Please choose another time.`,
        );
        return;
      }
    }

    setCustomTimeError(null);
    if (onCustomTime) {
      onCustomTime(`${snapToStep(customStartTime)}:00`, `${snapToStep(customEndTime)}:00`);
    }
    setShowCustomTimeDialog(false);
  }, [
    onCustomTime,
    customStartTime,
    customEndTime,
    dayWindows,
    selectedDate,
    currentDate,
    currentTimeInMinutes,
    isFullyBlockedWindow,
  ]);

  // Calculate day-level capacity for selected department
  const totalJobsForDay = useMemo(() => {
    if (!departmentId) return 0;
    const total = Object.values(windowEventCounts).reduce(
      (sum, deptCounts) => sum + (deptCounts[departmentId] || 0),
      0,
    );
    // windowEventCounts subtracts the job being rescheduled; it's still a real
    // booking, so add it back here for the "jobs booked" label (mirrors the
    // per-window +1 re-add). Only when the edited event is on this date + dept
    // and falls in a window — i.e. it was actually subtracted.
    const editedJobOnDay =
      !!existingEvent &&
      existingEvent.dateStarted?.split(' ')[0] === selectedDate &&
      (existingEvent.departmentId || 0) === departmentId &&
      dayWindowsDescending.some((w) => isEventInWindow(existingEvent, w));
    return total + (editedJobOnDay ? 1 : 0);
  }, [windowEventCounts, departmentId, existingEvent, selectedDate, dayWindowsDescending]);

  const standbyTechs = useMemo(() => {
    return techs.filter((tech) => tech.activity === 'Standby').length;
  }, [techs]);

  // Calculate progress bar fill percentage and color
  const capacityRatio =
    totalJobsForDay > 0 ? Math.min((standbyTechs / totalJobsForDay) * 100, 100) : 100;
  const capacityColor =
    standbyTechs >= totalJobsForDay
      ? 'bg-green-500'
      : standbyTechs > 0
        ? 'bg-yellow-500'
        : 'bg-red-500';

  return (
    <div className="space-y-6">
      {/* All Windows Past Warning */}
      {allWindowsPast && departmentId && !loading && dayWindows.length > 0 && (
        <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <span className="text-xl" role="img" aria-label="Warning">
              ⚠️
            </span>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">No available time windows</h3>
              <p className="mt-1 text-sm text-amber-700">
                {selectedDate < currentDate
                  ? 'This date is in the past. '
                  : 'All scheduled time windows for today have passed. '}
                Please select a future date or use a custom time if scheduling for later today.
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleNextDay}
                  className="bg-amber-600 text-white hover:bg-amber-700"
                >
                  Go to Next Day →
                </Button>
                {onCustomTime && selectedDate === currentDate && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleCustomTimeOpen}
                    className="border-amber-400 text-amber-700 hover:bg-amber-100"
                  >
                    Set Custom Time
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Day Pagination with Capacity Indicator */}
      <div className="rounded-lg border bg-white p-4">
        <div className="flex items-center justify-between">
          <Button type="button" variant="outline" size="sm" onClick={handlePreviousDay}>
            ← Previous Day
          </Button>
          <div className="flex flex-col items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-lg font-semibold text-gray-900 hover:text-blue-600 hover:underline"
                >
                  {format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')}
                  <ChevronDown className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="theme-light w-auto p-0" align="center">
                <Calendar
                  mode="single"
                  selected={parseISO(selectedDate)}
                  defaultMonth={parseISO(selectedDate)}
                  onSelect={(d) => {
                    if (!d) return;
                    onChangeDate(format(d, 'yyyy-MM-dd'));
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {dateBreakout && <CallWindowBreakout {...dateBreakout} />}
            {/* Day Capacity Bar */}
            {departmentId && !loading && dayWindows.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-600">
                  {totalJobsForDay} jobs booked
                </span>
                <div className="h-2 w-48 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full transition-all ${capacityColor}`}
                    style={{ width: `${capacityRatio}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-gray-600">
                  {standbyTechs} techs available
                </span>
              </div>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={handleNextDay}>
            Next Day →
          </Button>
        </div>
      </div>

      {/* Business/After Hours Tabs */}
      {departmentId && !loading && dayWindows.length > 0 && (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant={activeTab === 'business' ? 'default' : 'outline'}
            onClick={() => setActiveTab('business')}
            size="sm"
            disabled={businessWindows.length === 0}
          >
            Business
          </Button>
          <Button
            type="button"
            variant={activeTab === 'after' ? 'default' : 'outline'}
            onClick={() => setActiveTab('after')}
            size="sm"
            disabled={afterHoursWindows.length === 0}
          >
            After Hours
          </Button>
        </div>
      )}

      {/* No Department Selected Message */}
      {!departmentId && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-center">
          <p className="text-sm font-medium text-yellow-800">
            Please select a department to view technician availability
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && departmentId && (
        <div className="flex justify-center py-10">
          <LoadingIcon className="size-8" />
        </div>
      )}

      {/* Daily job-type cap reached — a day-level block, so it sits above the whole grid
          rather than on any one card. Every card below is disabled in this state. */}
      {!loading && departmentId && dailyCapState?.reached && (
        <div className="mb-4 rounded-lg border-2 border-red-400 bg-red-50 p-3">
          <div className="flex items-start gap-2">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <div className="text-sm">
              <p className="font-semibold text-red-700">
                Daily {dailyCapState.jobTypeName} cap reached — {dailyCapState.used} of{' '}
                {dailyCapState.max} confirmed
              </p>
              <p className="text-xs text-red-600">
                This department has hit its {dailyCapState.jobTypeName} limit for this day. No time
                window can take another one — choose a different day or job type.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Time Window Cards */}
      {!loading && departmentId && filteredWindows.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredWindows.map((window) => {
            const isPast = isWindowPast(window, selectedDate, currentDate, currentTimeInMinutes);

            const isSelected = selectedWindow?.id === window.id;
            const isRecommended = recommendedWindow === window.id;

            const { onStandby, finishingSoon } = calculateTechAvailability(window);

            // Calculate jobs and techs for this window (ONLY for selected department)
            const windowCounts = windowEventCounts[window.id] || {};
            const totalJobs = departmentId ? windowCounts[departmentId] || 0 : 0;
            const existingEventDate = existingEvent?.dateStarted?.split(' ')[0];
            const existingEventMatchesWindow =
              !!existingEvent &&
              existingEventDate === selectedDate &&
              (existingEvent.departmentId || 0) === (departmentId || 0) &&
              existingEventWindow?.id === window.id;
            const jobsBooked = totalJobs + (existingEventMatchesWindow ? 1 : 0);

            // Per-job-type breakdown for the selected department.
            // Add the edited event back (mirrors jobsBooked) so the breakdown sums to "Jobs N".
            const deptBreakdown: Record<string, number> =
              departmentId && windowJobTypeBreakdown[window.id]
                ? { ...(windowJobTypeBreakdown[window.id][departmentId] ?? {}) }
                : {};
            if (existingEventMatchesWindow) {
              const editedJobType = existingEvent?.jobType || 'Unspecified';
              deptBreakdown[editedJobType] = (deptBreakdown[editedJobType] || 0) + 1;
            }
            const jobTypeBreakdown = Object.entries(deptBreakdown)
              .map(([jobType, count]) => ({ jobType, count }))
              .filter((item) => item.count > 0)
              .sort((a, b) => b.count - a.count);

            // Get actual tech counts (unique technicians assigned)
            const techCounts = windowTechCounts[window.id] || {};
            const techsAssigned = departmentId ? techCounts[departmentId] || 0 : 0;

            const blocked = isFullyBlockedWindow(window.startTime, window.endTime);

            // Effective limits for this date: a date override if one exists, else this
            // department's weekly override, else the window's base values (departments share
            // the global window shapes).
            const { windowCount: effectiveWindowCount, maintMax: effectiveMaintMax } =
              resolveWindowLimits(
                window,
                windowLimitOverrides,
                windowLimitDateOverrides,
                selectedDate,
              );

            // Soft limit: window has reached its overall max jobs. Still selectable, overbook.
            // windowCount 0 means "no max configured" (the column default), not "no jobs
            // allowed" -- the limits dialog floors its stepper at 1 so 0 isn't reachable
            // from the UI.
            const atCapacity = effectiveWindowCount > 0 && jobsBooked >= effectiveWindowCount;

            // jobsBooked adds the edited event back, so a job already here takes no new slot
            // and keeping it here is not an overbook.
            const wouldOverbook = atCapacity && !existingEventMatchesWindow;

            // Hard limit: booking a maintenance job into a window that has hit its
            // maintenance limit. maintMax -1 = no limit, 0 = none.
            const confirmedMaint = departmentId
              ? windowMaintConfirmedCounts[window.id]?.[departmentId] ?? 0
              : 0;
            const maintLimitReached =
              bookingJobTypeId === MAINTENANCE_JOB_TYPE_ID &&
              effectiveMaintMax >= 0 &&
              confirmedMaint >= effectiveMaintMax;

            // Calculate confidence level
            let confidence: 'high' | 'medium' | 'low' = 'low';
            if (onStandby >= 2) {
              confidence = 'high';
            } else if (onStandby >= 1) {
              confidence = 'medium';
            }

            // Calculate arrival estimate
            let arrivalEstimate = '';
            if (!isPast) {
              if (onStandby > 0) {
                arrivalEstimate = 'Can arrive in 15-30 min';
              } else if (finishingSoon > 0) {
                arrivalEstimate = 'Can arrive in 45-60 min';
              }
            }

            return (
              <JobWindowCard
                key={window.id}
                window={window}
                jobsBooked={jobsBooked}
                techsAssigned={techsAssigned}
                techsOnStandby={onStandby}
                techsFinishingSoon={finishingSoon}
                jobTypeBreakdown={jobTypeBreakdown}
                isRecommended={isRecommended}
                confidence={confidence}
                arrivalEstimate={arrivalEstimate}
                isPast={isPast}
                isSelected={isSelected}
                isBlocked={blocked}
                atCapacity={wouldOverbook}
                capacityMax={effectiveWindowCount}
                maintLimitReached={maintLimitReached}
                dailyCapReached={!!dailyCapState?.reached}
                onSelect={() => onSelectWindow(window)}
              />
            );
          })}
        </div>
      )}

      {/* No Windows Available */}
      {!loading && departmentId && filteredWindows.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <p className="text-sm text-gray-600">
            No {activeTab === 'business' ? 'business hours' : 'after hours'} time windows available
            for this day
          </p>
        </div>
      )}

      {/* Custom Time Section */}
      {onCustomTime && (
        <div className="mt-4 space-y-3 border-t pt-4">
          {/* Custom Time Warning Banner - shown when custom time is past */}
          {isCustomTime && isCurrentCustomTimePast && (
            <div className="rounded-lg border-2 border-red-400 bg-red-50 p-3">
              <div className="flex items-center gap-2">
                <span className="text-red-500" role="img" aria-label="Warning">
                  ⚠️
                </span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">Custom time has passed</p>
                  <p className="text-xs text-red-600">
                    The selected time (
                    {currentStartTime ? formatTimeCondensed(currentStartTime) : ''}–
                    {currentEndTime ? formatTimeCondensed(currentEndTime) : ''}
                    {isCustomTimeOvernight ? ' next day' : ''}) is no longer available. Please
                    select a new time or change the date.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Custom Time Active Indicator */}
          {isCustomTime && !isCurrentCustomTimePast && (
            <div className="rounded-lg border-2 border-blue-400 bg-blue-50 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-blue-500">{isCustomTimeOvernight ? '🌙' : '🕐'}</span>
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      {isCustomTimeOvernight ? 'Overnight Booking' : 'Custom Time Selected'}
                    </p>
                    <p className="text-xs text-blue-600">
                      {currentStartTime ? formatTimeCondensed(currentStartTime) : ''}–
                      {currentEndTime ? formatTimeCondensed(currentEndTime) : ''}
                      {isCustomTimeOvernight ? ' (next day)' : ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Custom Time Button */}
          <Button
            type="button"
            variant={isCustomTime ? 'default' : 'outline'}
            onClick={handleCustomTimeOpen}
            className={`w-full ${
              isCustomTime
                ? isCurrentCustomTimePast
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
                : ''
            }`}
          >
            {isCustomTime
              ? isCurrentCustomTimePast
                ? '⚠️ Update Custom Time'
                : '🕐 Modify Custom Time'
              : 'Set Custom Time'}
          </Button>
        </div>
      )}

      {/* Custom Time Dialog */}
      <Dialog open={showCustomTimeDialog} onOpenChange={setShowCustomTimeDialog}>
        <DialogContent className="theme-light bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Custom Time</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Date context info */}
            <div className="rounded-md border border-gray-200 bg-white p-2 text-center text-sm text-gray-600">
              Scheduling for: {format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')}
              {selectedDate <= currentDate}
            </div>

            {/* Error Message */}
            {customTimeError && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                <div className="flex items-center gap-2">
                  <span role="img" aria-label="Warning">
                    ⚠️{' '}
                  </span>
                  <span>{customTimeError}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Start Time</Label>
              <TimePicker
                date={timeToDate(customStartTime)}
                onChange={(next) => {
                  const value = dateToTime(next);
                  if (value) handleStartTimeChange(value);
                }}
                granularity="minute"
                hourCycle={12}
                step={TIME_STEP_MINUTES}
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <TimePicker
                date={timeToDate(customEndTime)}
                onChange={(next) => {
                  const value = dateToTime(next);
                  if (value) handleEndTimeChange(value);
                }}
                granularity="minute"
                hourCycle={12}
                step={TIME_STEP_MINUTES}
              />
            </div>

            {/* Overnight booking indicator */}
            {isOvernightRange(customStartTime, customEndTime) && (
              <div className="rounded-md bg-blue-50 p-2 text-xs text-blue-700">
                <span className="font-medium">
                  <span role="img" aria-label="Moon">
                    🌙{' '}
                  </span>
                  Overnight booking:
                </span>
                Ends at {formatTimeCondensed(customEndTime)} on the following day
              </div>
            )}

            {/* Real-time validation hint */}
            {selectedDate <= currentDate &&
              (() => {
                const isPast = isTimeRangePast(
                  customStartTime,
                  customEndTime,
                  selectedDate,
                  currentDate,
                  currentTimeInMinutes,
                );

                if (isPast) {
                  return (
                    <p className="text-xs text-amber-600">
                      <span role="img" aria-label="Clock">
                        ⏰{' '}
                      </span>
                      Current time is {format(now, 'h:mm a')} — this time range has passed
                    </p>
                  );
                }
                return null;
              })()}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowCustomTimeDialog(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleCustomTimeConfirm}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
